import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, ImageOff } from "lucide-react";
import { useTranslation } from "../../i18n";
import {
  fetchReportData,
  generateSummary,
  useReportData,
  useTotal,
  useWidgetData,
} from "./reportApi";
import { BarChartViz, LineChartViz, PieChartViz, ComparisonViz } from "./charts";
import {
  PALETTE,
  formatValue,
  metricFormat,
  prettyLabel,
  SOURCE_LABELS,
  type WidgetConfig,
} from "./types";

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
function inline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-gray-900">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <Fragment key={i}>{p}</Fragment>
    )
  );
}

function RichText({ text }: { text: string }) {
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-1.5 text-sm leading-relaxed text-gray-700">
      {lines.map((line, i) => {
        if (line.startsWith("## "))
          return (
            <h3 key={i} className="mt-1 text-lg font-bold text-gray-900">
              {inline(line.slice(3))}
            </h3>
          );
        if (line.startsWith("# "))
          return (
            <h2 key={i} className="text-xl font-bold text-gray-900">
              {inline(line.slice(2))}
            </h2>
          );
        if (!line.trim()) return <div key={i} className="h-1.5" />;
        return <p key={i}>{inline(line)}</p>;
      })}
    </div>
  );
}

const Loading = () => (
  <div className="flex min-h-[6rem] items-center justify-center">
    <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
  </div>
);

function MetricValue({
  source,
  metric,
  label,
  filters,
  accentColor,
  big,
}: {
  source: WidgetConfig["source"];
  metric: string;
  label: string;
  filters?: Record<string, unknown>;
  accentColor?: string;
  big?: boolean;
}) {
  const { data, isLoading } = useTotal(source, metric, filters);
  const value = data?.total ?? 0;
  return (
    <div>
      <p className={`font-bold leading-none ${big ? "text-4xl" : "text-2xl"}`} style={{ color: accentColor }}>
        {isLoading ? "…" : formatValue(value, metricFormat(source, metric))}
      </p>
      <p className="mt-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main widget dispatcher
// ---------------------------------------------------------------------------
export function WidgetView({
  config,
  editing = false,
  onChange,
}: {
  config: WidgetConfig;
  editing?: boolean;
  onChange?: (patch: Partial<WidgetConfig>) => void;
}) {
  const { t } = useTranslation();
  const color = PALETTE[config.color];
  const fmt = (n: number) => formatValue(n, metricFormat(config.source, config.metric));

  switch (config.type) {
    case "metric":
      return (
        <div className="flex h-full flex-col justify-center py-2">
          <MetricValue
            source={config.source}
            metric={config.metric}
            label={config.title}
            filters={config.filters}
            accentColor={color}
            big
          />
        </div>
      );

    case "kpi_row":
      return (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {config.metrics.map((m, i) => (
            <div key={i} className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-3">
              <MetricValue source={m.source} metric={m.metric} label={m.label} />
            </div>
          ))}
        </div>
      );

    case "bar":
      return <ChartBlock config={config}>{(rows) => <BarChartViz rows={rows} color={color} format={fmt} orientation={config.orientation} showValues={config.showValues} />}</ChartBlock>;

    case "day_of_week":
      return <ChartBlock config={config}>{(rows) => <BarChartViz rows={rows} color={color} format={fmt} orientation="horizontal" showValues={config.showValues} />}</ChartBlock>;

    case "line":
      return <ChartBlock config={config}>{(rows) => <LineChartViz rows={rows} color={color} format={fmt} />}</ChartBlock>;

    case "pie":
      return <ChartBlock config={config}>{(rows) => <PieChartViz rows={rows} color={color} format={fmt} donut={config.donut} />}</ChartBlock>;

    case "table":
      return <TableBlock config={config} />;

    case "status_table":
      return <RankedTableBlock config={config} />;

    case "comparison":
      return <ComparisonBlock config={config} />;

    case "text":
      if (editing && onChange)
        return (
          <textarea
            className="min-h-[6rem] w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={config.text}
            onChange={(e) => onChange({ text: e.target.value })}
            placeholder={t("reportBuilder.textPlaceholder", "Write your text. Use ## for a heading and **bold** for emphasis.")}
          />
        );
      return <RichText text={config.text} />;

    case "ai_summary":
      return <AiSummaryBlock config={config} editing={editing} onChange={onChange} />;

    case "image":
      return <ImageBlock config={config} editing={editing} onChange={onChange} />;

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Data-backed blocks
// ---------------------------------------------------------------------------
function ChartBlock({
  config,
  children,
}: {
  config: WidgetConfig;
  children: (rows: { label: string; value: number }[]) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useWidgetData(config);
  if (isLoading) return <Loading />;
  if (error) return <p className="py-6 text-center text-sm text-red-600">{t("reportBuilder.loadFailed", "Couldn’t load data.")}</p>;
  return <>{children(data?.rows ?? [])}</>;
}

function TableBlock({ config }: { config: WidgetConfig }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useWidgetData(config);
  const fmt = (n: number) => formatValue(n, metricFormat(config.source, config.metric));
  if (isLoading) return <Loading />;
  if (error) return <p className="py-6 text-center text-sm text-red-600">{t("reportBuilder.loadFailed", "Couldn’t load data.")}</p>;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const dimLabel = config.groupBy ? prettyLabel(config.groupBy) : t("reportBuilder.group", "Group");
  const metricLabel = prettyLabel(config.metric === "count" ? SOURCE_LABELS[config.source] : config.metric);

  if (!rows.length) return <p className="py-6 text-center text-sm text-gray-300">{t("reportBuilder.noDataSelection", "No data for this selection")}</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{dimLabel}</th>
            <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">{metricLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-50">
              <td className="px-3 py-2 text-sm text-gray-800">{r.label}</td>
              <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">{fmt(r.value)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-200">
            <td className="px-3 py-2 text-sm font-semibold text-gray-900">{t("reportBuilder.total", "Total")}</td>
            <td className="px-3 py-2 text-right text-sm font-bold text-gray-900">{fmt(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function RankedTableBlock({ config }: { config: WidgetConfig }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useWidgetData(config);
  const fmt = (n: number) => formatValue(n, metricFormat(config.source, config.metric));
  if (isLoading) return <Loading />;
  if (error) return <p className="py-6 text-center text-sm text-red-600">{t("reportBuilder.loadFailed", "Couldn’t load data.")}</p>;
  const rows = data?.rows ?? [];
  const max = Math.max(1, ...rows.map((r) => r.value));

  if (!rows.length) return <p className="py-6 text-center text-sm text-gray-300">{t("reportBuilder.noDataSelection", "No data for this selection")}</p>;

  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-32 shrink-0">
            <span className="text-sm font-medium text-gray-700">{r.label}</span>
          </div>
          <div className="h-2.5 flex-1 rounded-sm bg-gray-100">
            <div className="h-2.5 rounded-sm" style={{ width: `${(r.value / max) * 100}%`, backgroundColor: PALETTE[config.color] }} />
          </div>
          <span className="w-14 shrink-0 text-right text-sm font-semibold text-gray-900">{fmt(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ComparisonBlock({ config }: { config: WidgetConfig }) {
  const { t } = useTranslation();
  const { range, hospitalId } = useReportData();
  const b = config.seriesB;
  const fmt = (n: number) => formatValue(n, metricFormat(config.source, config.metric));
  const filters = { ...config.filters, ...(hospitalId && !config.filters.hospital_id ? { hospital_id: hospitalId } : {}) };
  const a = useQuery({
    queryKey: ["report-data", "cmpA", config.source, config.metric, config.groupBy, range, filters, config.limit],
    queryFn: () =>
      fetchReportData({ source: config.source, metric: config.metric, groupBy: config.groupBy, dateRange: range, filters, limit: config.limit }),
    staleTime: 30_000,
  });
  const bq = useQuery({
    enabled: !!b,
    queryKey: ["report-data", "cmpB", b?.source, b?.metric, config.groupBy, range, filters, config.limit],
    queryFn: () =>
      fetchReportData({ source: b!.source, metric: b!.metric, groupBy: config.groupBy, dateRange: range, filters, limit: config.limit }),
    staleTime: 30_000,
  });
  if (a.isLoading || (b && bq.isLoading)) return <Loading />;
  return (
    <ComparisonViz
      rowsA={a.data?.rows ?? []}
      rowsB={bq.data?.rows ?? []}
      labelA={prettyLabel(config.metric)}
      labelB={b ? b.label : t("reportBuilder.seriesB", "Series B")}
      format={fmt}
    />
  );
}

function AiSummaryBlock({
  config,
  editing,
  onChange,
}: {
  config: WidgetConfig;
  editing?: boolean;
  onChange?: (patch: Partial<WidgetConfig>) => void;
}) {
  const { t } = useTranslation();
  const { snapshot } = useReportData();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function generate() {
    setBusy(true);
    setErr(null);
    try {
      const widgets = snapshot().filter((w) => w.rows.length > 0 || w.total);
      const res = await generateSummary(config.title, widgets);
      onChange?.({ text: res.text });
    } catch (e) {
      setErr((e as Error).message || t("reportBuilder.summaryFailed", "Failed to generate summary."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {config.text ? (
        <RichText text={config.text} />
      ) : (
        <p className="text-sm italic text-gray-400">
          {editing ? t("reportBuilder.aiSummaryHint", "Click “Generate” to write observations from your report’s data.") : t("reportBuilder.noSummary", "No summary generated.")}
        </p>
      )}
      {editing && onChange && (
        <div className="no-print mt-3 flex items-center gap-2">
          <button
            onClick={generate}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Sparkles size={14} /> {busy ? t("reportBuilder.generating", "Generating…") : config.text ? t("reportBuilder.regenerate", "Regenerate") : t("reportBuilder.generate", "Generate")}
          </button>
          {err && <span className="text-xs text-red-600">{err}</span>}
        </div>
      )}
    </div>
  );
}

function ImageBlock({
  config,
  editing,
  onChange,
}: {
  config: WidgetConfig;
  editing?: boolean;
  onChange?: (patch: Partial<WidgetConfig>) => void;
}) {
  const { t } = useTranslation();
  function onFile(file: File | null) {
    if (!file || !onChange) return;
    const reader = new FileReader();
    reader.onload = () => onChange({ imageUrl: reader.result as string });
    reader.readAsDataURL(file);
  }

  if (editing && onChange) {
    return (
      <div className="space-y-2">
        {config.imageUrl && (
          <img src={config.imageUrl} alt={config.caption || t("reportBuilder.reportImage", "Report image")} className="mx-auto max-h-48 rounded-lg object-contain" />
        )}
        <input
          type="file"
          accept="image/*"
          className="block w-full text-xs text-gray-600 file:mr-2 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-blue-700"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <input
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder={t("reportBuilder.imageUrlPlaceholder", "Image URL (optional)")}
          value={config.imageUrl && !config.imageUrl.startsWith("data:") ? config.imageUrl : ""}
          onChange={(e) => onChange({ imageUrl: e.target.value || null })}
        />
        <input
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder={t("reportBuilder.captionPlaceholder", "Caption (optional)")}
          value={config.caption}
          onChange={(e) => onChange({ caption: e.target.value })}
        />
      </div>
    );
  }
  if (!config.imageUrl)
    return (
      <div className="flex min-h-[8rem] flex-col items-center justify-center gap-2 text-gray-300">
        <ImageOff size={28} />
        <span className="text-sm">{t("reportBuilder.noImageUploaded", "No image uploaded")}</span>
      </div>
    );
  return (
    <figure>
      <img src={config.imageUrl} alt={config.caption || t("reportBuilder.reportImage", "Report image")} className="mx-auto max-h-80 rounded-lg object-contain" />
      {config.caption && <figcaption className="mt-2 text-center text-xs text-gray-500">{config.caption}</figcaption>}
    </figure>
  );
}
